import { supabase } from './supabase'

/**
 * Appointments — the things you have to *be at*.
 *
 * Everything else the calendar holds has a date and is something you do: a bill
 * to pay, a letter to answer, a chore to tick off. An appointment is different
 * in a way that matters for the model: it has a **time**, it belongs to
 * particular people, and you never tick it off — it simply passes.
 *
 * Date and time are kept apart, never one timestamp. 14:00 has to stay 14:00
 * whatever a server thinks the timezone is; one `timestamptz` would let a
 * daylight-saving change move a dentist appointment by an hour, and that is the
 * kind of bug you find out about in a waiting room. See 0038.
 */

export interface Appointment {
  id: string
  spaceId: string
  title: string
  location: string | null
  notes: string | null
  onDate: string
  /** null = the whole day. "Sunday my sister visits" is a real appointment. */
  startTime: string | null
  endTime: string | null
  people: string[]
}

export async function loadAppointments(
  from: string,
  to: string,
): Promise<Appointment[]> {
  const { data, error } = await supabase
    .from('appointments')
    .select('id, space_id, title, location, notes, on_date, start_time, end_time')
    .is('deleted_at', null)
    .gte('on_date', from)
    .lte('on_date', to)
    .order('on_date')
  if (error) throw error
  if (!data || data.length === 0) return []

  // Row-level security decides which rows came back; this only fills in who is
  // going, and a member can read that only for their own.
  const { data: people } = await supabase
    .from('appointment_people')
    .select('appointment_id, user_id')
    .in('appointment_id', data.map((a) => a.id as string))

  const byId = new Map<string, string[]>()
  for (const row of people ?? []) {
    const id = row.appointment_id as string
    byId.set(id, [...(byId.get(id) ?? []), row.user_id as string])
  }

  return data.map((row) => ({
    id: row.id as string,
    spaceId: row.space_id as string,
    title: row.title as string,
    location: (row.location as string | null) ?? null,
    notes: (row.notes as string | null) ?? null,
    onDate: row.on_date as string,
    startTime: trimSeconds(row.start_time as string | null),
    endTime: trimSeconds(row.end_time as string | null),
    people: byId.get(row.id as string) ?? [],
  }))
}

/** Postgres hands back "14:00:00"; a person reads "14:00". */
function trimSeconds(time: string | null): string | null {
  return time ? time.slice(0, 5) : null
}

export async function saveAppointment(appointment: {
  id?: string | null
  spaceId: string
  title: string
  onDate: string
  startTime: string | null
  endTime: string | null
  location: string | null
  notes: string | null
  people: string[]
}): Promise<string> {
  const { data, error } = await supabase.rpc('save_appointment', {
    p_id: appointment.id ?? null,
    p_space_id: appointment.spaceId,
    p_title: appointment.title,
    p_on_date: appointment.onDate,
    p_start_time: appointment.startTime || null,
    p_end_time: appointment.endTime || null,
    p_location: appointment.location,
    p_notes: appointment.notes,
    p_people: appointment.people,
  })
  if (error) throw new Error(readable(error.message))
  return data as string
}

export async function deleteAppointment(id: string): Promise<void> {
  const { error } = await supabase.rpc('delete_appointment', { p_id: id })
  if (error) throw new Error(readable(error.message))
}

function readable(message: string): string {
  if (message.includes('waar gaat de afspraak over')) return 'Geef de afspraak een naam.'
  if (message.includes('een afspraak heeft een dag')) return 'Kies een dag.'
  if (message.includes('appointments_time_order')) {
    return 'De eindtijd ligt voor de begintijd.'
  }
  return 'Dat lukte niet — ' + message.slice(0, 120)
}

/** "14:00 – 14:30", "14:00", or "hele dag". */
export function describeWhen(a: Appointment): string {
  if (!a.startTime) return 'hele dag'
  return a.endTime ? `${a.startTime} – ${a.endTime}` : a.startTime
}

/** Earliest first; whole-day appointments lead the day, as they do in life. */
export function byTime(a: Appointment, b: Appointment): number {
  if (a.onDate !== b.onDate) return a.onDate.localeCompare(b.onDate)
  if (!a.startTime && !b.startTime) return a.title.localeCompare(b.title)
  if (!a.startTime) return -1
  if (!b.startTime) return 1
  return a.startTime.localeCompare(b.startTime)
}
